/**
 * The audit page's store-view switcher: which store's problems the page
 * shows. Defaults to the logged-in user's home store (seeded by the page);
 * anyone can switch to another store or "All stores" — a convenience default,
 * not a security boundary (Ian, 2026-09-11). The backend narrows every count,
 * list, search and download to the same store, so the page stays
 * self-consistent.
 *
 * Desktop renders a tab strip under the page title — tokens copied from the
 * canonical PhotoListTabs (pages/photos/DailyPhotoListPage.tsx). Phones get a
 * native select instead, for the same reason PhotoListTabs does: a long strip
 * forces a horizontal hunt, and the OS picker reads better in the lane.
 */
import React from "react"

import { AuditRooftopOption } from "../../api/audit"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"

/** Sentinel meaning "no store selected — show the whole group". */
export const ALL_ROOFTOPS = "all"
/** The chosen store is remembered for this visit, per login: picking a store
 * on one page carries to every other page with store tabs, and each new visit
 * starts at the home store again. Per visit (sessionStorage) so a home store
 * assigned later is honoured; per login so a shared browser, or switching
 * accounts, never hands one person another's choice — Ian switched his own
 * account to Toyota and still landed on the store his admin login had picked
 * (2026-10-09). */
const REMEMBERED_STORE_PREFIX = "invi_audit_rooftop"

function rememberedStoreKey(userId: number): string {
  return `${REMEMBERED_STORE_PREFIX}:${userId}`
}

/** The store this login chose earlier in the visit, or null. */
export function readRememberedStore(userId: number | null | undefined): string | null {
  if (userId == null) return null
  return sessionStorage.getItem(rememberedStoreKey(userId))
}

export function rememberStore(userId: number | null | undefined, slug: string): void {
  if (userId == null) return
  sessionStorage.setItem(rememberedStoreKey(userId), slug)
}

interface Props {
  options: AuditRooftopOption[]
  /** A rooftop slug, or ALL_ROOFTOPS. */
  value: string
  onChange: (next: string) => void
}

export default function AuditStoreTabs({ options, value, onChange }: Props) {
  const isMobile = useIsMobile()

  // Tabs carry the short store names the audit rows themselves use
  // ("Toyota", "Mazda / Kia") — the full rooftop names ("Dolan Fernley
  // CDJR") triple the strip's width without telling an operator anything
  // the page hasn't already said.
  const tabs = [
    { slug: ALL_ROOFTOPS, label: "All stores" },
    ...options.map((option) => ({ slug: option.slug, label: option.audit_label })),
  ]

  if (isMobile) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: THEME.spacing.sm,
          marginBottom: THEME.spacing.md,
        }}
      >
        <label
          htmlFor="audit-store-selector"
          style={{
            fontSize: 13,
            color: THEME.colors.neutral.light.textPrimary,
            fontWeight: 500,
          }}
        >
          Store:
        </label>
        <select
          id="audit-store-selector"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
            fontSize: 13,
            borderRadius: THEME.borderRadius.md,
            border: `1px solid ${THEME.colors.silver}`,
            backgroundColor: THEME.colors.neutral.light.surface,
            color: THEME.colors.neutral.light.textPrimary,
            fontFamily: THEME.fonts.primary,
            cursor: "pointer",
            flex: 1,
            minWidth: 0,
          }}
        >
          {tabs.map(({ slug, label }) => (
            <option key={slug} value={slug}>
              {label}
            </option>
          ))}
        </select>
      </div>
    )
  }

  const tabStyle = (slug: string): React.CSSProperties => {
    const isActive = slug === value
    return {
      padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
      cursor: "pointer",
      backgroundColor: "transparent",
      border: "none",
      fontFamily: THEME.fonts.primary,
      borderBottom: isActive
        ? `3px solid ${THEME.colors.navy}`
        : "3px solid transparent",
      color: isActive
        ? THEME.colors.navy
        : THEME.colors.neutral.light.textSecondary,
      fontWeight: isActive ? 600 : 400,
      fontSize: 13,
      userSelect: "none" as const,
      transition: "color 0.15s, border-color 0.15s",
      whiteSpace: "nowrap" as const,
    }
  }

  return (
    <div
      style={{
        display: "flex",
        gap: 0,
        borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
        marginBottom: THEME.spacing.md,
        overflowX: "auto",
      }}
    >
      {tabs.map(({ slug, label }) => (
        <button
          key={slug}
          type="button"
          style={tabStyle(slug)}
          onClick={() => onChange(slug)}
          aria-pressed={slug === value}
        >
          {label}
        </button>
      ))}
    </div>
  )
}
